import type { TagType, UserInstrumentClassificationDto } from "@eu.mdotm.api/sphere";
import type { MinimumDialogProps } from "$root/components/spawnable/type";
import { tagColorMap } from "$root/ui-lib/table/colors";
import { TagBadge } from "$root/ui-lib/table/custom-column/tags/Tag";
import { qualifier } from "$root/utils";
import { useQueryReactor, useQueryNoRefetch } from "$root/third-party-integrations/react-query/hooks";
import {
	type Option,
	type OrderBy,
	type TableColumn,
	BaseTable,
	CircularProgressBar,
	Controller,
	DebouncedSearchInput,
	Dialog,
	DialogFooter,
	LocalOverlay,
	sortRows,
	TableDataCell,
	useSelectableTableColumn,
} from "@mdotm/shadcn/legacy/components";
import { Button, Select, SubmitButton } from "@mdotm/shadcn/mdotm/components";
import { type MultiSelectCtx, useMultiSelect, useSearchable } from "@mdotm/shadcn/legacy/headless";
import type { MaybePromise } from "@mdotm/utils/promise";
import { type SpawnResult, adaptAnimatedNodeProvider, spawn } from "@mdotm/shadcn/legacy/react-extensions";
import { builtInSort } from "@mdotm/shadcn/legacy/utils";
import { Map } from "immutable";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { type IdentifiedClassification, cleanClassificationId, userClassificationId } from "./classification-id";

type ClassificationDialogProps = {
	selectableUserClassifications: UserInstrumentClassificationDto[];
	/** When omitted, all `fieldType === "TAG"` classifications are selectable regardless of `tagType`. */
	tagType?: TagType;
	mode?: "checkbox" | "radio";
	onSubmit(payload: { classification: IdentifiedClassification; selectedOptions: string[] }): void;
	optionsProvider(classificationUuid: string): MaybePromise<Array<string>>;
	disabledRowIds?: string[];
	initialValue?: {
		classificationUuid: string;
		selectedOptions: string[];
	};
} & MinimumDialogProps;

function ClassificationDialog({
	selectableUserClassifications,
	show,
	tagType,
	mode,
	optionsProvider,
	onAnimationStateChange,
	onClose,
	onSubmit,
	disabledRowIds,
	initialValue,
}: ClassificationDialogProps) {
	const { t } = useTranslation();

	const selectableOptionsByTagType = useMemo(
		() =>
			selectableUserClassifications.filter(
				(classification) =>
					classification.fieldType === "TAG" && (tagType === undefined || classification.tagType === tagType),
			),
		[selectableUserClassifications, tagType],
	);

	const [selectableTag, setSelectableTag] = useState<string[] | undefined>(undefined);
	const [classification, setClassification] = useState<UserInstrumentClassificationDto>(
		initialValue?.classificationUuid
			? (selectableOptionsByTagType.find((x) => x.classificationUuid === initialValue?.classificationUuid) ?? {})
			: (selectableOptionsByTagType.at(0) ?? {}),
	);

	const classificationMultiSelectCtx = useMultiSelect<string>({
		selectable: selectableTag,
	});

	useEffect(() => {
		if (initialValue?.selectedOptions && initialValue?.classificationUuid && show) {
			classificationMultiSelectCtx.setSelection(
				initialValue.selectedOptions.map((tag) => userClassificationId(initialValue.classificationUuid, tag)),
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [initialValue?.selectedOptions, show]);

	return (
		<Dialog
			size="xxlarge"
			show={show}
			onAnimationStateChange={onAnimationStateChange}
			onClose={onClose}
			header="Select"
			onSubmitAsync={() => {
				const { classificationUuid } = classification;
				if (!classificationUuid) {
					return;
				}
				const selection = classificationMultiSelectCtx.selection.valueSeq().toArray();
				const selectedOptions = selection.map(
					(selectionId) => cleanClassificationId(selectionId, classificationUuid) /** circular dep */,
				);

				onSubmit({
					classification: { ...classification, classificationUuid },
					selectedOptions,
				});
			}}
			footer={
				<DialogFooter
					primaryAction={
						<SubmitButton
							palette="primary"
							disabled={classification.classificationUuid ? undefined : true}
							data-qualifier={qualifier.component.button("Dialog/Submit")}
						>
							{t("BUTTON.DONE")}
						</SubmitButton>
					}
					neutralAction={
						<Button palette="tertiary" onClick={onClose} data-qualifier={qualifier.component.button("Dialog/Cancel")}>
							{t("BUTTON.CANCEL")}
						</Button>
					}
				/>
			}
		>
			<div className="max-h-[400px] h-[400px] min-h-0 overflow-hidden flex flex-col">
				<ClassificationOptionsTable
					selectedClassificationUuid={classification.classificationUuid}
					onChangeClassification={(newClassification) => {
						if (newClassification) {
							setClassification(newClassification);
							classificationMultiSelectCtx.reset();
						}
					}}
					onClassificationLoad={(availableOptions) => {
						if (availableOptions && availableOptions.length > 0) {
							const opts = availableOptions.map((x) => userClassificationId(classification.classificationUuid!, x));
							const filteredOpts =
								disabledRowIds && disabledRowIds.length > 0 ? opts.filter((x) => !disabledRowIds.includes(x)) : opts;
							setSelectableTag(filteredOpts);
							return;
						}
						setSelectableTag(undefined);
					}}
					optionsProvider={optionsProvider}
					multiSelectCtx={classificationMultiSelectCtx}
					selectableUserClassifications={selectableOptionsByTagType}
					mode={mode}
				/>
			</div>
		</Dialog>
	);
}

type ClassificationOptionsTableProps = {
	selectedClassificationUuid?: string;
	selectableUserClassifications: UserInstrumentClassificationDto[];
	onChangeClassification(userClassification?: UserInstrumentClassificationDto): void;
	optionsProvider(classificationUuid: string): MaybePromise<Array<string>>;
	mode?: "checkbox" | "radio";
	multiSelectCtx: MultiSelectCtx<string>;
	onClassificationLoad?(availableOptions: string[] | undefined): void;
};

export function ClassificationOptionsTable({
	selectedClassificationUuid,
	selectableUserClassifications,
	onChangeClassification,
	optionsProvider,
	mode = "checkbox",
	multiSelectCtx,
	onClassificationLoad,
}: ClassificationOptionsTableProps): React.JSX.Element {
	const [search, setSearch] = useState("");

	const availableClassificationByTagTypeMap = Map<string, UserInstrumentClassificationDto>(
		selectableUserClassifications.flatMap((x) => (x.classificationUuid ? [[x.classificationUuid, x]] : [])),
	);

	const selectableClassificationOptions = useMemo(
		() =>
			selectableUserClassifications.map(
				(x): Option<string> => ({
					label: x.name ?? "untitled",
					value: x.classificationUuid!,
				}),
			),
		[selectableUserClassifications],
	);

	const queryClassificationOptions = useQueryNoRefetch(["queryClassificationOptions", selectedClassificationUuid], {
		enabled: selectedClassificationUuid != null,
		queryFn() {
			return optionsProvider(selectedClassificationUuid!);
		},
	});

	useQueryReactor(queryClassificationOptions, {
		onSuccess: onClassificationLoad,
	});

	const { data: availableOptions, isFetching: isClassificationOptionsFetching } = queryClassificationOptions;

	const rows = useMemo(() => availableOptions ?? [], [availableOptions]);
	const { filtered } = useSearchable({
		collection: rows,
		query: search,
		extractFn: (opt) => opt,
	});

	const checkboxColumnData = useSelectableTableColumn({
		mode,
		rows,
		selectBy: (opt) => (selectedClassificationUuid ? userClassificationId(selectedClassificationUuid, opt) : opt),
		filteredRows: filtered,
		multiSelectCtx,
	});

	const currentClassification = selectedClassificationUuid
		? availableClassificationByTagTypeMap.get(selectedClassificationUuid)
		: undefined;

	const columns = useMemo<TableColumn<string>[]>(
		() => [
			checkboxColumnData.column,
			{
				header: "Name",
				content: (option, cellProps) => (
					<TableDataCell {...cellProps}>
						<TagBadge label={option} color={classificationOptionColor(currentClassification, option)} />
					</TableDataCell>
				),
				name: "option",
				sortFn: builtInSort,
			},
		],
		[checkboxColumnData.column, currentClassification],
	);

	return (
		<>
			<div className="flex gap-2 mb-4">
				<DebouncedSearchInput
					data-qualifier="ClassificationOptionsTable/Search"
					externalQuery={search}
					onChange={setSearch}
					placeholder="Search..."
					inputAppearance={{ className: "max-w-[280px]" }}
				/>
				<Select
					data-qualifier="ClassificationOptionsTable/Select/Options"
					triggerDataAttrs={{
						"data-qualifier": "ClassificationOptionsTable/Select/Trigger",
					}}
					options={selectableClassificationOptions}
					value={selectedClassificationUuid}
					onChange={(newClassificationUuid) => {
						if (newClassificationUuid) {
							const selectedClassification = availableClassificationByTagTypeMap.get(newClassificationUuid);
							onChangeClassification(selectedClassification);
						}
					}}
				/>
			</div>
			<div className="relative min-h-0 flex-1 flex flex-col">
				<LocalOverlay
					show={isClassificationOptionsFetching}
					className="z-10"
					style={{
						background: "transparent",
					}}
				>
					<CircularProgressBar value="indeterminate" />
				</LocalOverlay>
				<Controller value={defaultReportOrderBy}>
					{({ value: orderBy, onChange: onOrderByChange }) => (
						<BaseTable
							columns={columns}
							rows={search ? filtered : sortRows({ rows: filtered, columns, orderByArr: orderBy })}
							orderBy={search ? undefined : orderBy}
							onOrderByChange={onOrderByChange}
							onRowClick={(option) => {
								if (
									selectedClassificationUuid &&
									!checkboxColumnData.multiSelectCtx.disabled(userClassificationId(selectedClassificationUuid, option))
								) {
									checkboxColumnData.toggle(userClassificationId(selectedClassificationUuid, option));
								}
							}}
							className="max-h-[462px] min-h-[320px]"
							noDataText="No tags available"
						/>
					)}
				</Controller>
			</div>
		</>
	);
}

const defaultReportOrderBy: Array<OrderBy<"option">> = [{ columnName: "option", direction: "asc" }];

export function findClassificationByOption(
	value: string | null | undefined,
	classifications: UserInstrumentClassificationDto[],
): UserInstrumentClassificationDto | undefined {
	if (value == null) {
		return undefined;
	}
	return classifications.find((c) => c.options?.some((o) => o.option === value));
}

export function classificationOptionColor(
	classification: UserInstrumentClassificationDto | undefined,
	optionValue: string | null | undefined,
): string | undefined {
	if (optionValue == null) {
		return undefined;
	}
	const match = classification?.options?.find((o) => o.option === optionValue);
	return match?.color ? tagColorMap[match.color]?.badgeColor() : undefined;
}

type SpawnClassificationDialogProps = Omit<ClassificationDialogProps, "show" | "onClose">;
export function spawnClassificationDialog(props: SpawnClassificationDialogProps): SpawnResult<void> {
	return spawn<void>(
		adaptAnimatedNodeProvider(({ show, resolve, onHidden }) => (
			<ClassificationDialog
				{...props}
				show={show}
				onAnimationStateChange={(state) => state === "hidden" && onHidden()}
				onClose={() => resolve()}
				onSubmit={(payload) => {
					props.onSubmit(payload);
					resolve();
				}}
			/>
		)),
	);
}
